import { defineStore } from "pinia";
import { identity, useLocalStorage } from "@vueuse/core";
import {
  defaultPerContentSettings,
  DefaultSettingsFlag,
  GlobalSettings,
  PerContentSettings,
  standardizeGlobalStorageKeys,
} from "@/helpers/settings";
import { usePublicationStore } from "./publication";
import { useWpsStore } from "./wps";

const defaultGlobalSettings: GlobalSettings = {
  theme: "day",
  [DefaultSettingsFlag]: true,
};

/**
 * Using Proxy object allows us to NOT merges all the layers of settings
 * but instead retrieving them dynamically.
 *
 * This way, we're able to only store the settings
 * that are actually changed from their default value.
 */
type ProxyTarget = () => Record<string | typeof DefaultSettingsFlag, unknown>[];
const settingFetcher: ProxyHandler<ProxyTarget> = {
  has: (target, prop) => {
    const defaults = target().find((s) => s[DefaultSettingsFlag]);
    return !!defaults && prop in defaults;
  },
  ownKeys: (target) => {
    const defaults = target().find((s) => s[DefaultSettingsFlag]);
    return Object.keys(defaults);
  },
  getOwnPropertyDescriptor(target, p) {
    const defaults = target().find((s) => s[DefaultSettingsFlag]);
    return Object.getOwnPropertyDescriptor(defaults, p);
  },
  get: (target, prop) => {
    if (typeof prop !== "string") {
      return undefined;
    }

    const values = target()
      .map((settings) => {
        const value = settings[prop];
        return value !== undefined ? value : null;
      })
      .filter((value) => value !== null);

    return values[0];
  },
  set(target, prop, newValue) {
    if (typeof prop !== "string") {
      return false;
    }

    target().forEach((settings) => {
      if (settings[DefaultSettingsFlag]) return;
      settings[prop] = newValue;
    });
    return true;
  },
};

export const useSettingsStore = defineStore("settings", () => {
  const wps = useWpsStore();

  const storage = useLocalStorage<GlobalSettings>(
    "DM__reader-user-settings",
    {} as GlobalSettings,
    { flush: "post" }
  );

  const globalSettings = new Proxy(
    (() => [storage.value, defaultGlobalSettings]) as ProxyTarget,
    settingFetcher
  ) as unknown as GlobalSettings;

  standardizeGlobalStorageKeys(storage);

  const publicationStore = usePublicationStore();

  const emptyDefault = { [DefaultSettingsFlag]: true };
  const perContentSettings = new Proxy(() => {
    const linkType = wps.client?.media.linkType;
    if (!linkType) {
      return [publicationStore.storage.settings || {}];
    }
    if (!storage.value[linkType]) {
      storage.value[linkType] = {};
    }
    return [
      publicationStore.storage.settings,
      storage.value[linkType],
      defaultPerContentSettings[linkType],
      // Ensure default settings are always included
      emptyDefault,
    ].filter(identity); // exclude empty settings
  }, settingFetcher) as PerContentSettings;

  return {
    global: globalSettings,
    perContent: perContentSettings,
  };
});
